import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import useFaqOperations, {
  UseFaqOperationsReturn,
} from 'src/components/Faqs/CustomHooks/useFaqOperations';
import { useMaintenanceGuides } from 'src/SharedCustomHooks/UseMaintenanceGuides/useMaintenanceGuides';
import { variables } from 'src/Tenants';
import { PATHS } from 'src/common/constants';
import SuiteAssets from 'src/pages/Suite/SuiteAssets';
import SuiteServiceRequests from 'src/pages/Suite/SuiteServiceRequests';
import SuiteMessages from 'src/pages/Suite/SuiteMessages';
import SuiteDocuments from 'src/pages/Suite/SuiteDocuments';
import SuiteMaintainenceGuide from 'src/pages/Suite/SuiteMaintainanceGuide';
import SuiteUsers from 'src/pages/Suite/SuiteUsers/SuiteUsers';
import SuiteFaqs from 'src/pages/Suite/SuiteFaqs';
import { SuiteSpark } from 'src/pages/Suite/SuiteSpark';
import SuiteNotes from 'src/pages/Suite/SuiteNotes/SuiteNotes';
import { UseNotesAPIReturn } from 'src/SharedCustomHooks/UseNotesAPI/useNotesAPI';
import SuiteWalkThroughs from 'src/pages/Suite/SuiteWalkThroughs';
import { useMemo, ReactNode, useEffect } from 'react';
import {
  useMessagesAPI,
  UseMessagesAPIReturn,
} from 'src/pages/ServiceRequest/CustomHooks/useMessagesAPI';
import {
  useSuiteServiceRequestsOrchestrator,
  UseSuiteServiceRequestsOrchestratorReturn,
} from 'src/pages/Suite/CustomHooks/SuiteServiceRequests/useSuiteServiceRequestsOrchestrator';

export interface SuiteTabItem {
  label: string;
  count: string;
  content: ReactNode;
  localLabel?: string;
}

export type UseBaseTabItemsReturn = SuiteTabItem[];
export type UseAdminTabItemsReturn = SuiteTabItem[];
export type UseStrataManagerTabItemsReturn = SuiteTabItem[];

interface TabConfigProps {
  suite: any;
  property: any;
  history: any;
  counts: any;
  branding: any;
  match: any;
  properties: any;
  contractFiles: any;
  sparkContractsCount: number;
  sparkDeposits: any;
  sparkDepositsCount: number;
  messagesLabel: string;
  notesAPI: UseNotesAPIReturn;
  onFetchSuiteData: () => void;
  refreshSuiteCounts: () => Promise<void>;
  isEventShow?: boolean;
  propertyManagerDetails?: any;
  calculateAggregatedDocCount: (a: number, b: number) => string;
}

const useSuiteFaqData = (suite: any) => {
  const user: any = useSelector(selectAuthenticatedUser);
  const userRole: any = user?.roles[0].role?.name;

  const faqOperations: UseFaqOperationsReturn = useFaqOperations({
    itemId: suite?.id,
    userId: user?.id,
    userRole,
    entityType: 'Suite',
    assetId: null,
  });

  return { faqs: faqOperations.faqs, faqOperations };
};

export const useBaseTabItems = (
  props: TabConfigProps
): UseBaseTabItemsReturn => {
  const { faqs, faqOperations } = useSuiteFaqData(props.suite);

  const {
    messages,
    loadingMessages,
    isSending,
    fetchMessagesFromAPI,
    postMessageToAPI,
  }: UseMessagesAPIReturn = useMessagesAPI();

  useEffect(() => {
    if (props.suite?.id) {
      fetchMessagesFromAPI(props.suite.id, 'Suite');
    }
  }, [props.suite?.id]);

  const {
    guides: maintenanceGuides = [],
    loading: maintenanceGuidesLoading,
    error,
    setSearchTerm,
    searchTerm,
  } = useMaintenanceGuides(props.suite?.id, { source: 'Suite', userId: '' });

  const serviceRequestsOrchestrator: UseSuiteServiceRequestsOrchestratorReturn =
    useSuiteServiceRequestsOrchestrator({
      suiteId: props.suite?.id,
      navigateTo: PATHS.SUITES_VIEW.replace(
        ':id',
        `${props.suite?.propertyId}&${props.suite?.id}`
      ),
      navLabel: `${variables.Suite} ${props.suite?.number ?? ''}`,
    });

  return useMemo((): UseBaseTabItemsReturn => {
    const items: SuiteTabItem[] = [
      {
        label: variables.roomsAndItems,
        count: props.counts.assetsCount?.toString() || '0',
        content: (
          <SuiteAssets
            suite={props.suite}
            property={props.property}
            history={props.history}
          />
        ),
      },
      {
        label: props.branding.text.serviceRequest + 's',
        count: serviceRequestsOrchestrator.serviceRequests.length.toString(),
        content: (
          <SuiteServiceRequests
            suite={props.suite}
            orchestrator={serviceRequestsOrchestrator}
          />
        ),
      },
      {
        label: props.messagesLabel,
        count: messages.length.toString(), // REAL-TIME COUNT
        content: (
          <SuiteMessages
            suite={props.suite}
            property={props.property}
            messages={messages}
            loadingMessages={loadingMessages}
            isSending={isSending}
            postMessageToAPI={postMessageToAPI}
          />
        ),
      },
      {
        label: 'Manuals & Documents',
        count: props.counts.documentsCount
          ? props.calculateAggregatedDocCount(
              props.sparkContractsCount,
              props.counts.documentsCount
            )
          : props.sparkContractsCount.toString(),
        content: (
          <SuiteDocuments
            suite={props.suite}
            properties={props.properties}
            contractFiles={props.contractFiles}
          />
        ),
      },
      {
        label: 'Maintenance Reminders',
        count: maintenanceGuides.length.toString(),
        content: (
          <SuiteMaintainenceGuide
            maintenanceGuides={maintenanceGuides}
            loading={maintenanceGuidesLoading}
            error={error}
            setSearchTerm={setSearchTerm}
            searchTerm={searchTerm}
          />
        ),
      },
      {
        label: 'People',
        count: props.counts.peopleCount?.toString() || '0',
        content: (
          <SuiteUsers
            suite={props.suite}
            property={props.property}
            onFetchSuiteData={props.onFetchSuiteData}
          />
        ),
      },
      {
        label: 'FAQs',
        count: faqs ? faqs.length.toString() : '0',
        content: (
          <SuiteFaqs suite={props.suite} faqOperations={faqOperations} />
        ),
      },
    ];

    if (props.suite?.isSpark && props.sparkDepositsCount > 0) {
      items.push({
        label: 'Deposits',
        count: props.sparkDepositsCount.toString(),
        content: (
          <SuiteSpark suite={props.suite} sparkDeposits={props.sparkDeposits} />
        ),
      });
    }

    return items;
  }, [
    props,
    messages,
    loadingMessages,
    isSending,
    maintenanceGuides,
    faqs,
    serviceRequestsOrchestrator,
  ]);
};

export const useAdminTabItems = (
  props: TabConfigProps
): UseAdminTabItemsReturn => {
  const { faqs, faqOperations } = useSuiteFaqData(props.suite);

  const {
    messages,
    loadingMessages,
    isSending,
    fetchMessagesFromAPI,
    postMessageToAPI,
  }: UseMessagesAPIReturn = useMessagesAPI();

  useEffect(() => {
    if (props.suite?.id) {
      fetchMessagesFromAPI(props.suite.id, 'Suite');
    }
  }, [props.suite?.id]);

  const {
    guides: maintenanceGuides = [],
    loading: maintenanceGuidesLoading,
    error,
    setSearchTerm,
    searchTerm,
  } = useMaintenanceGuides(props.suite?.id, { source: 'Suite', userId: '' });

  const serviceRequestsOrchestrator: UseSuiteServiceRequestsOrchestratorReturn =
    useSuiteServiceRequestsOrchestrator({
      suiteId: props.suite?.id,
      navigateTo: PATHS.SUITES_VIEW.replace(
        ':id',
        `${props.suite?.propertyId}&${props.suite?.id}`
      ),
      navLabel: `${variables.Suite} ${props.suite?.number ?? ''}`,
    });

  return useMemo((): UseAdminTabItemsReturn => {
    const items: SuiteTabItem[] = [
      {
        localLabel: 'Rooms and Items',
        label: variables.roomsAndItems,
        count: props.counts.assetsCount?.toString() || '0',
        content: (
          <SuiteAssets
            suite={props.suite}
            property={props.property}
            history={props.history}
          />
        ),
      },
      {
        localLabel: props.branding.text.serviceRequest + 's',
        label: props.branding.text.serviceRequest + 's',
        count: serviceRequestsOrchestrator.serviceRequests.length.toString(),
        content: (
          <SuiteServiceRequests
            suite={props.suite}
            orchestrator={serviceRequestsOrchestrator}
          />
        ),
      },
      {
        localLabel: 'Manuals & Documents',
        label: 'Manuals & Documents',
        count: props.counts.documentsCount
          ? props.calculateAggregatedDocCount(
              props.sparkContractsCount,
              props.counts.documentsCount
            )
          : props.sparkContractsCount.toString(),
        content: (
          <SuiteDocuments
            suite={props.suite}
            properties={props.properties}
            contractFiles={props.contractFiles}
          />
        ),
      },
      {
        localLabel: props.messagesLabel,
        label: 'Messages',
        count: messages.length.toString(),
        content: (
          <SuiteMessages
            suite={props.suite}
            property={props.property}
            messages={messages}
            loadingMessages={loadingMessages}
            isSending={isSending}
            postMessageToAPI={postMessageToAPI}
          />
        ),
      },
      {
        localLabel: 'Maintenance Reminders',
        label: 'Maintenance Reminders',
        count: maintenanceGuides.length.toString(),
        content: (
          <SuiteMaintainenceGuide
            maintenanceGuides={maintenanceGuides}
            loading={maintenanceGuidesLoading}
            error={error}
            setSearchTerm={setSearchTerm}
            searchTerm={searchTerm}
          />
        ),
      },
      {
        localLabel: 'People',
        label: 'People',
        count: props.counts.peopleCount?.toString() || '0',
        content: (
          <SuiteUsers
            suite={props.suite}
            property={props.property}
            onFetchSuiteData={props.onFetchSuiteData}
          />
        ),
      },
      {
        localLabel: 'FAQs',
        label: 'FAQs',
        count: faqs ? faqs.length.toString() : '0',
        content: (
          <SuiteFaqs suite={props.suite} faqOperations={faqOperations} />
        ),
      },
      {
        localLabel: 'Notes',
        label: 'Notes',
        count: props.counts.notesCount?.toString() || '0',
        content: (
          <SuiteNotes
            suite={props.suite}
            notesAPI={props.notesAPI}
            onFetchSuiteData={props.onFetchSuiteData}
          />
        ),
      },
      {
        localLabel: 'WalkThrough',
        label: 'WalkThrough',
        count: props.counts.walkthroughsCount?.toString() || '0',
        content: (
          <SuiteWalkThroughs
            suite={props.suite}
            property={props.property}
            isEventShow={props.isEventShow}
            propertyManagerDetails={props.propertyManagerDetails}
            refreshSuiteCounts={props.refreshSuiteCounts}
          />
        ),
      },
    ];

    if (props.suite?.isSpark && props.sparkDepositsCount > 0) {
      items.push({
        localLabel: 'Spark Deposits',
        label: 'Deposits',
        count: props.sparkDepositsCount.toString(),
        content: (
          <SuiteSpark suite={props.suite} sparkDeposits={props.sparkDeposits} />
        ),
      });
    }

    return items;
  }, [props, messages, loadingMessages, isSending, maintenanceGuides, faqs]);
};

/* Strata Managers get a deliberately narrow tab set (Rooms/Items, Service Requests,
 * Messages, Walkthrough). This is its own hook rather than a filtered adminTabItems so
 * we don't fire the FAQ / maintenance-guide fetches that role can't access — only the
 * two data hooks these four tabs actually need are wired here. */
export const useStrataManagerTabItems = (
  props: TabConfigProps
): UseStrataManagerTabItemsReturn => {
  const {
    messages,
    loadingMessages,
    isSending,
    fetchMessagesFromAPI,
    postMessageToAPI,
  }: UseMessagesAPIReturn = useMessagesAPI();

  useEffect(() => {
    if (props.suite?.id) {
      fetchMessagesFromAPI(props.suite.id, 'Suite');
    }
  }, [props.suite?.id]);

  const serviceRequestsOrchestrator: UseSuiteServiceRequestsOrchestratorReturn =
    useSuiteServiceRequestsOrchestrator({
      suiteId: props.suite?.id,
      navigateTo: PATHS.SUITES_VIEW.replace(
        ':id',
        `${props.suite?.propertyId}&${props.suite?.id}`
      ),
      navLabel: `${variables.Suite} ${props.suite?.number ?? ''}`,
    });

  return useMemo(
    (): UseStrataManagerTabItemsReturn => [
      {
        localLabel: 'Rooms and Items',
        label: variables.roomsAndItems,
        count: props.counts.assetsCount?.toString() || '0',
        content: (
          <SuiteAssets
            suite={props.suite}
            property={props.property}
            history={props.history}
          />
        ),
      },
      {
        localLabel: props.branding.text.serviceRequest + 's',
        label: props.branding.text.serviceRequest + 's',
        count: serviceRequestsOrchestrator.serviceRequests.length.toString(),
        content: (
          <SuiteServiceRequests
            suite={props.suite}
            orchestrator={serviceRequestsOrchestrator}
          />
        ),
      },
      {
        localLabel: props.messagesLabel,
        label: 'Messages',
        count: messages.length.toString(),
        content: (
          <SuiteMessages
            suite={props.suite}
            property={props.property}
            messages={messages}
            loadingMessages={loadingMessages}
            isSending={isSending}
            postMessageToAPI={postMessageToAPI}
          />
        ),
      },
      {
        localLabel: 'Notes',
        label: 'Notes',
        count: props.counts.notesCount?.toString() || '0',
        content: (
          <SuiteNotes
            suite={props.suite}
            notesAPI={props.notesAPI}
            onFetchSuiteData={props.onFetchSuiteData}
          />
        ),
      },
      {
        localLabel: 'WalkThrough',
        label: 'WalkThrough',
        count: props.counts.walkthroughsCount?.toString() || '0',
        content: (
          <SuiteWalkThroughs
            suite={props.suite}
            property={props.property}
            isEventShow={props.isEventShow}
            propertyManagerDetails={props.propertyManagerDetails}
            refreshSuiteCounts={props.refreshSuiteCounts}
          />
        ),
      },
    ],
    [props, messages, loadingMessages, isSending, serviceRequestsOrchestrator]
  );
};
